import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { MapPin, Building2, CheckCircle2, Sparkles, Navigation, ShieldCheck } from 'lucide-react';
import { AthleteProfile } from '../types';
import { StateCitySelector } from './common/StateCitySelector';

interface LocationOnboardingModalProps {
  isOpen: boolean;
  user: AthleteProfile;
  onConfirmLocation: (state: string, city: string) => void;
  onClose?: () => void;
}

export const LocationOnboardingModal: React.FC<LocationOnboardingModalProps> = ({
  isOpen,
  user,
  onConfirmLocation,
  onClose,
}) => {
  const [selectedState, setSelectedState] = useState<string>(user?.registeredState || 'NY');
  const [selectedCity, setSelectedCity] = useState<string>(user?.registeredCity || 'New York');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCity.trim()) return;
    onConfirmLocation(selectedState.trim(), selectedCity.trim());
  };

  const quickCities = [
    { city: 'New York', state: 'NY' },
    { city: 'Brooklyn', state: 'NY' },
    { city: 'Los Angeles', state: 'CA' },
    { city: 'Miami', state: 'FL' },
    { city: 'Chicago', state: 'IL' },
    { city: 'Houston', state: 'TX' },
  ];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md">
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="bg-indigo-950 border-2 border-lime-400/50 rounded-[2.5rem] p-6 sm:p-8 max-w-lg w-full text-white space-y-6 shadow-2xl relative overflow-hidden"
        >
          {/* Top Decorative Banner Background */}
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-lime-400 via-cyan-400 to-indigo-500" />

          <div className="text-center space-y-2">
            <div className="w-16 h-16 rounded-3xl bg-lime-400 text-black mx-auto flex items-center justify-center font-black text-2xl shadow-xl shadow-lime-400/30">
              <MapPin className="w-8 h-8 stroke-[2.5]" />
            </div>
            <div className="inline-flex items-center space-x-1.5 px-3 py-1 bg-lime-400/20 text-lime-300 text-[10px] font-mono font-black uppercase tracking-widest rounded-full border border-lime-400/40">
              <Sparkles className="w-3 h-3" />
              <span>REGIONAL ONBOARDING STEP 1 OF 1</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black italic uppercase tracking-tight text-white pt-1">
              Confirm Your Location 📍
            </h2>
            <p className="text-xs text-indigo-200/80 font-medium max-w-md mx-auto leading-relaxed">
              Welcome, <strong className="text-white">{user?.name}</strong>! Confirm your home city and state to automatically filter local court maps, regional leaderboards, and nearby tournament brackets.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Quick City Presets */}
            <div>
              <label className="block text-[11px] font-black uppercase text-indigo-300 tracking-wider mb-2">
                Popular Regional Hubs:
              </label>
              <div className="grid grid-cols-3 gap-2">
                {quickCities.map((item) => (
                  <button
                    key={`${item.city}-${item.state}`}
                    type="button"
                    onClick={() => {
                      setSelectedCity(item.city);
                      setSelectedState(item.state);
                    }}
                    className={`p-2 rounded-xl text-xs font-bold transition flex items-center justify-between border ${
                      selectedCity === item.city && selectedState === item.state
                        ? 'bg-lime-400 text-black border-lime-300 shadow-lg font-black'
                        : 'bg-indigo-900/60 text-indigo-200 border-white/10 hover:border-white/30'
                    }`}
                  >
                    <span>{item.city}</span>
                    <span className="text-[10px] opacity-75">{item.state}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom State & City Input */}
            <div className="pt-2">
              <StateCitySelector
                selectedState={selectedState}
                selectedCity={selectedCity}
                onStateChange={(newState, defaultCity) => {
                  setSelectedState(newState);
                  setSelectedCity(defaultCity);
                }}
                onCityChange={(newCity) => setSelectedCity(newCity)}
                selectClassName="w-full bg-indigo-900/90 border border-white/15 rounded-xl px-3 py-3 text-sm font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
                labelClassName="block text-xs font-black uppercase text-indigo-200 mb-1"
                autoDetectOnMount={true}
                enableAutoDetectButton={true}
              />
            </div>

            {/* Feature preview note */}
            <div className="p-3 bg-indigo-900/40 rounded-2xl border border-white/10 text-xs text-indigo-200/90 flex items-start space-x-2.5">
              <ShieldCheck className="w-4 h-4 text-lime-400 shrink-0 mt-0.5" />
              <p className="text-[11px] leading-tight">
                Your location setting automatically centers the Court Map, filters regional leaderboards, and unlocks localized pickup games. You can change this anytime in Profile Settings.
              </p>
            </div>

            <button
              type="submit"
              className="w-full py-4 bg-lime-400 hover:bg-lime-300 text-black font-black text-sm uppercase italic rounded-2xl shadow-xl shadow-lime-400/25 flex items-center justify-center space-x-2 transition tracking-wider"
            >
              <CheckCircle2 className="w-5 h-5 stroke-[2.5]" />
              <span>Confirm Location & Sync Local Features 📍</span>
            </button>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
